import { useState, useEffect, useMemo, useCallback } from "react";
import { FormTemplate, FormSection } from "../../../../types/forms";
import { Trash2, Plus, Loader2, Edit, ArrowLeft } from "lucide-react";
import AddSectionModal from "../../../../components/AddSectionModal";
import { useLocalStorage } from "../../../../hooks/useLocalStorage";
import { FormServiceRef } from "../../../../types/forms";
import { BusinessService } from "../../../../types/businessService";
import { userId } from "../../../../config";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/input2";
import { Textarea } from "../../../../components/ui/Textarea";
import ProgressBar from "../../../../components/ProgressBar";
import "../../../../styles/scrollbar.css";
import { useTemplateSave } from "../../../../hooks/useTemplateSave";
import ServicesModal from "./ServicesModal";
import { addSection } from "../../../../services/consultationform";
import { addSection as apiAddSection } from "../../../../services/consultationform";
import { deleteFieldOrSection as apiDeleteSection } from "../../../../services/consultationform";
import { getFormByUuid } from "../../../../services/consultationform";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import { reorderItems } from "../../../../services/consultationform";
import { getSunbeds, SunbedItem } from "../../../../services/sunbeds";
import { getBusinessServices } from "../../../../services/businessServicesApi";
import SunbedSelector from "./SunbedSelector";
import SectionEditor from "./SectionEditor";
import { useToast } from "../../../../hooks/useToast";

interface SidebarProps {
  template: FormTemplate;
  updateTemplate: (
    template: FormTemplate | ((prev: FormTemplate) => FormTemplate)
  ) => void;
  currentSection: number;
  setCurrentSection: (value: number) => void;
  deleteSection: (sectionId: number) => void;
  addSection: (section: FormSection) => void;
}


export default function Sidebar({
  template,
  updateTemplate,
  deleteSection,
  addSection,
}: SidebarProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const showToast = useToast();

  const servicesKey = `services_${userId}`;
  const [businessServices, setBusinessServices] = useState<BusinessService[]>([]);
  const [sunbeds, setSunbeds] = useState<SunbedItem[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | number | null>(null);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);

  const steps = useMemo(
    () => ["Title", "Description", "Service", "Sections", "Dummy"],
    []
  );
  const { saveTemplate, isSaving, message, setMessage } = useTemplateSave(
    template,
    updateTemplate
  );

  const filledSteps = useMemo(() => {
    let filled = 0;
    if (template.title.trim() && template.title !== "Untitled") filled++;
    if (template.description.trim()) filled++;
    if (template.services?.length) filled++;
    if (template.sections.length) filled++;
    return filled;
  }, [template]);

  useEffect(() => {
    setIsEdited(template.draft || false);

    // Fetch sunbeds
    const fetchSunbeds = async () => {
      try {
        const response = await getSunbeds();
        if (response.data) {
          setSunbeds(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch sunbeds:", error);
      }
    };
    fetchSunbeds();

    const fetchServices = async () => {
      try {
        const list = await getBusinessServices();
        setBusinessServices(list);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      }
    };
    fetchServices();
  }, []);

  // Removed the useEffect for message timeout as we use toast now
  // Keeping message handling from useTemplateSave just in case, but toast logic is primary

  const handleAddSection = async (section: FormSection) => {
    if (!template.uuid) {
      addSection(section);
      markEdited();
      return;
    }

    // Map section type to form_template_id (must match useFormBuilder templateIdMap: 5 = heading)
    const typeToTemplateId: Record<string, number> = {
      contact: 1,
      medical: 2,
      marketing: 3,
      question: 4,
      heading: 5,
      text: 6,
      policy: 7,
      disclaimer: 8,
    };

    const formTemplateId = typeToTemplateId[section.type as string] || 1;

    try {
      const response = await apiAddSection(template.uuid, formTemplateId);
      // The response structure provided by the user has the section under the 'section' key
      if (response && response.section) {
        // Ensure the returned section has the correct type for LivePreview mapping
        const createdSection: FormSection = {
          ...response.section,
          type: section.type || response.section.type, // Fallback to passed type if API returns null
          title: section.title || response.section.title,
        };
        addSection(createdSection);
      }
      markEdited();
    } catch (err) {
      console.error("Failed to add section via API:", err);
      showToast.error("Failed to add section");
    }
  };



  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(template.sections);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    const updatedItems = items.map((section, index) => ({
      ...section,
      order: index + 1,
    }));

    updateTemplate((prev) => ({ ...prev, sections: updatedItems }));
    markEdited();

    const payload = items.map((section, index) => ({
      id: Number(section.id),
      order: index + 1,
    }));
    try {
      await reorderItems(template.uuid!, { sections: payload });
      console.log("Sections order saved successfully on backend");
    } catch (err) {
      console.error("Error saving section order:", err);
    }
  };

  const markEdited = useCallback(() => {
    setIsEdited(true);
    updateTemplate((prev) => ({ ...prev, draft: true, saved: false }));
  }, [updateTemplate]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.slice(0, 30);
      updateTemplate((prev) => ({ ...prev, title: value }));
      markEdited();
    },
    [updateTemplate, markEdited]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value.slice(0, 100);
      updateTemplate((prev) => ({ ...prev, description: value }));
      markEdited();
    },
    [updateTemplate, markEdited]
  );

  const handleSunbedToggle = useCallback(
    (sunbed: SunbedItem) => {
      updateTemplate((prev) => {
        const currentSunbeds = prev.sunbeds || [];
        const exists = currentSunbeds.some((s) => s.id === sunbed.id);

        const updatedSunbeds = exists
          ? currentSunbeds.filter((s) => s.id !== sunbed.id)
          : [...currentSunbeds, sunbed];

        return { ...prev, sunbeds: updatedSunbeds };
      });

      markEdited();
    },
    [updateTemplate, markEdited]
  );
  const handleServicesApply = useCallback(
    (selectedList: FormServiceRef[]) => {
      updateTemplate((prev) => ({
        ...prev,
        services: selectedList.map((s) => ({
          ...s,
          id: String(s.id),
          title: s.title,
          name: s.name ?? s.title,
          slug: s.slug ?? s.title.toLowerCase().replace(/\s+/g, "-"),
        })),
      }));
      markEdited();
    },
    [updateTemplate, markEdited]
  );

  const handleSaveTemplate = useCallback(() => {
    console.log("-------------------------", template);
    if (!template.uuid) {
      console.error("Cannot save: UUID is missing");
      showToast.error("Cannot save: UUID is missing");
      return;
    }

    const finalTemplate: FormTemplate = {
      ...template,
      userId,
      saved: true,
      draft: false,
    };

    updateTemplate(finalTemplate);
    saveTemplate(finalTemplate);
    setIsEdited(false);
  }, [template, saveTemplate, updateTemplate, showToast]);


  const handleDeleteSection = useCallback(
    async (sectionId: number | string) => {
      // Remove section locally immediately for responsiveness
      updateTemplate((prev) => ({
        ...prev,
        sections: prev.sections.filter((s) => s.id !== sectionId),
      }));

      // Call API if we have a UUID
      if (template.uuid) {
        try {
          await apiDeleteSection("section", Number(sectionId));
          showToast.success("Section deleted successfully");
        } catch (err) {
          console.error("Failed to delete section:", err);
          showToast.error("Failed to delete section");
        }
      } else {
        showToast.success("Section removed (form not saved)");
      }

      markEdited();
    },
    [template.uuid, updateTemplate, markEdited, showToast]
  );

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Progress Bar */}
      <ProgressBar steps={steps} currentStep={filledSteps} />

      {/* Header */}
      <div className="bg-white px-5 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-gray-900">
            Form Setup
          </h2>
        </div>
        <div className="-mx-5 mt-3 h-[2.5px] bg-gray-200 rounded-full shadow-sm" />
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 transition-all duration-300">

        {/* Title Field */}
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1">
            Form Title <span className="text-gray-400">(max 30 chars)</span>
          </label>
          <Input
            value={template.title}
            onChange={handleTitleChange}
            placeholder="Enter title..."
            className="text-sm h-8 rounded-md"
          />
        </div>

        {/* Description Field */}
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1">
            Description <span className="text-gray-400">(max 100 chars)</span>
          </label>
          <Textarea
            value={template.description}
            onChange={handleDescriptionChange}
            placeholder="Write a short description..."
            resize="none"
            className="text-sm h-16 rounded-md"
          />
        </div>

        {/* Services Section */}
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1">
            Services
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {(template.services || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {(template.services || []).map((s) => (
                  <span
                    key={s.id}
                    className="inline-flex items-center bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-[10px]"
                  >
                    {s.title || s.name}
                  </span>
                ))}
              </div>
            )}
            <Button
              type="button"
              onClick={() => setIsServicesModalOpen(true)}
              className="h-8 text-xs bg-[#885ABB] hover:bg-[#7748A4] text-white"
            >
              {(template.services || []).length > 0 ? "Change services" : "Select services"}
            </Button>
          </div>
          <ServicesModal
            isOpen={isServicesModalOpen}
            onClose={() => setIsServicesModalOpen(false)}
            services={businessServices}
            selected={template.services || []}
            onApply={handleServicesApply}
          />
        </div>

        {/* Sunbeds Section */}
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1">
            Sunbeds
          </label>
          <SunbedSelector
            sunbeds={sunbeds}
            selected={template.sunbeds || []}
            onToggle={handleSunbedToggle}
          />
        </div>

        {/* Sections List */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <p className="text-[12px] font-medium text-gray-600">Sections</p>
            <Button
              onClick={() => {
                setIsModalOpen(true);
                markEdited();
              }}
              className="px-2 border border-gray-300 bg-white text-gray-800 hover:bg-gray-100 h-[35px]"
            >
              <Plus size={12} /> Add
            </Button>
          </div>

          {activeSectionId === null ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sections">
                {(
                  provided: DroppableProvided,
                  snapshot: DroppableStateSnapshot
                ) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`space-y-1.5 ${snapshot.isDraggingOver
                      ? "bg-purple-50/40 rounded-md p-2"
                      : ""
                      }`}
                  >
                    {template.sections.length > 0 ? (
                      template.sections.map((section, index) => (
                        <Draggable
                          key={section.id}
                          draggableId={String(section.id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-all ${snapshot.isDragging
                                ? "bg-purple-50 shadow-md"
                                : ""
                                }`}
                            >
                              <div
                                {...provided.dragHandleProps}
                                className="flex-1 cursor-move"
                              >
                                <p className="text-[11px] text-[#885ABB] font-bold uppercase tracking-wider">
                                  {section.type}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {(section.type === "contact" || section.type === "question") && (
                                  <button
                                    onClick={() => setActiveSectionId(section.id)}
                                    className="text-gray-400 hover:text-[#885ABB]"
                                    title="Edit Section"
                                  >
                                    <Edit size={12} />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    handleDeleteSection(section.id);
                                    markEdited();
                                  }}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 italic text-center py-1">
                        No sections added
                      </p>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (() => {
            const section = template.sections.find((s) => s.id === activeSectionId);
            if (!section) return null;
            if (section.type === "heading" || section.type === "text") {
              return (
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <button onClick={() => setActiveSectionId(null)} className="text-gray-500 hover:text-gray-800">
                      <ArrowLeft size={16} />
                    </button>
                    <h3 className="font-semibold text-gray-800 uppercase tracking-wider text-xs">Section</h3>
                  </div>
                  <p className="text-xs text-gray-500 italic">Edit this section in the preview area on the right.</p>
                </div>
              );
            }
            return (
              <SectionEditor
                section={section}
                onBack={() => setActiveSectionId(null)}
                updateTemplate={updateTemplate}
                formUuid={template.uuid!}
              />
            );
          })()}
        </div>
      </div>

      {/* Save Button */}
      <div className="px-5 pb-4">
        <Button
          onClick={handleSaveTemplate}
          className={`w-full max-w-[140px] text-xs rounded-md h-8 flex items-center justify-center ${isSaving
            ? "bg-[#885ABB] cursor-not-allowed"
            : "bg-[#885ABB] hover:bg-purple-700"
            } text-white`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : isEdited ? (
            "Save Template"
          ) : (
            "Template Saved"
          )}
        </Button>
      </div>

      {/* Add Section Modal */}
      <AddSectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddSection}
        existingSections={template.sections}
      />
    </div>
  );
}
